import { useCallback, useState } from "react";
import { flushSync } from "react-dom";

import { Editor } from "tinymce";

import { type TextTemplate } from "services/types/text-templates.type";

import { getParsedWordProcessorValue } from "../word-processor-value.utils";
import { type TextTemplatesDialogValues } from "./text-templates-dialog.component";
import { getTextWithMergeTagsValues } from "./text-templates.utils";

type useTextTemplatesProps = {
  editorRef: React.MutableRefObject<Editor | null>;
  handleChangeValue: (html?: string | undefined, _editor?: Editor) => void;
  getMergeTagValue?: (mergeTag: string) => string | undefined | null;
  allowHtml?: boolean;
  maxLength?: number;
  replaceMergeTagName?: (mergeTag: string) => string;
  insertMergeTagValues?: boolean;
  getTemplateSubjectValue?: (subjectTemplate?: string | null) => string;
};

export type OnTemplateInsertedCallback =
  | ((template: TextTemplate | null) => void)
  | undefined;

export type SubmitTemplate = (
  values: TextTemplatesDialogValues,
  editorOverride?: Editor
) => void;

export type SubmitTemplateCallback = (
  onTemplateInserted: OnTemplateInsertedCallback
) => SubmitTemplate;

export default function useTextTemplates({
  editorRef,
  handleChangeValue,
  getMergeTagValue,
  allowHtml,
  maxLength,
  insertMergeTagValues = true,
  replaceMergeTagName,
  getTemplateSubjectValue
}: useTextTemplatesProps) {
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);

  const handleShowTemplatesDialog = useCallback(() => {
    setShowTemplatesDialog(true);
  }, []);

  const handleCloseTemplatesDialog = useCallback(() => {
    setShowTemplatesDialog(false);
  }, []);

  const handleSubmitTemplatesDialog: SubmitTemplateCallback = useCallback(
    (onTemplateInserted: OnTemplateInsertedCallback) => {
      const result: SubmitTemplate = (
        { template, needReplace }: TextTemplatesDialogValues,
        editorOverride?: Editor
      ) => {
        const editorToUse = editorOverride ?? editorRef.current;
        if (!editorToUse) {
          return;
        }
        const rawHtml = getParsedWordProcessorValue(template?.text).html;
        const html = getTextWithMergeTagsValues({
          text: rawHtml,
          getMergeTagValue,
          replaceMergeTagName,
          insertMergeTagValues,
          allowHtml
        });

        // Guard against empty resolved content: a `needReplace` with empty HTML
        // would call setContent("") and wipe the whole field (SN-18246). Only
        // touch the editor when there is real content to write, mirroring the
        // guard in word-processor-field's handleDropdownInsertTemplate.
        if (html.trim()) {
          // Ensure editor has focus so insertContent targets the correct location
          editorToUse.focus();
          if (needReplace) {
            editorToUse.setContent(html);
          } else {
            editorToUse.insertContent(html);
          }
          const contentToSync = editorToUse.getContent();
          flushSync(() => {
            handleChangeValue(contentToSync, editorToUse);
          });
        }
        handleCloseTemplatesDialog();
        onTemplateInserted?.(
          template
            ? {
                ...template,
                subject_template: getTemplateSubjectValue
                  ? getTemplateSubjectValue(template.subject_template)
                  : template.subject_template
              }
            : template
        );
      };

      return result;
    },
    [
      replaceMergeTagName,
      insertMergeTagValues,
      allowHtml,
      editorRef,
      getMergeTagValue,
      handleChangeValue,
      handleCloseTemplatesDialog,
      getTemplateSubjectValue
    ]
  );
  return {
    showTemplatesDialog,
    setShowTemplatesDialog,
    handleShowTemplatesDialog,
    handleCloseTemplatesDialog,
    handleSubmitTemplatesDialog
  };
}
